import { delay } from 'redux-saga';
import { apply, call, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { incentiveSaga } from './proactive/incentivesSaga';
import {
    botConvoStarted,
    botConvoStarting,
    convoStartError,
    removeConversation,
    visitorBlacklisted,
    newBotMessage,
    proactiveRemove,
    setBotTyping,
    setLocalSettings,
} from '../actions';
import * as api from '../actions/api';
import { getConvo, getLocation, getVisitor, getUi } from '../selectors/conversation';
import { getSettings } from '../selectors/settings';
import {
    ConvoWithMessageStartRequestAction,
    CtaClickedAction,
    NewBotMessagePayload,
    StartLockedPricingBotAction,
} from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { BotConvoStartedPayload } from '../typedefs/api';
import { ConversationState, VisitorState } from '../typedefs/store';
import { DICWindow } from '../typedefs/window';
import { instance as LocalStorageManager } from '../util/LocalStorageManager';

const PROACTIVE_DECLINE_TIMEOUT = 1000 * 60 * 60 * 24;

declare var window: DICWindow;

/**
 * If a conversation is started immediately when the app loads (like in the cars.com popup), the bootstrap
 * process might not be finished yet. This allows us to wait for it to finish.
 */
function* ensureAlreadyBootstrapped() {
    for (let i = 0; i < 40; i++) {
        const { bootstrapped } = yield select(getSettings);
        if (!bootstrapped) {
            yield call(delay, 250);
        } else {
            return;
        }
    }
    throw new Error('Never bootstrapped');
}

function* startConvo(
    isRetry: boolean,
    action: CtaClickedAction | ConvoWithMessageStartRequestAction,
): any {
    try {
        yield call(ensureAlreadyBootstrapped);

        const convo: ConversationState = yield select(getConvo);
        if (convo.active || (!convo.active && convo.ended)) return;
        const visitor: VisitorState = yield select(getVisitor);
        if (visitor.blacklisted) {
            return;
        }
        yield put(botConvoStarting());
        const { botId, customMetadata, extraContext } = yield select(getSettings);
        const { id: locationId } = yield select(getLocation);
        const response: { data: BotConvoStartedPayload } = yield call(
            api.startBotConvo,
            locationId,
            botId,
            visitor,
            customMetadata,
            extraContext,
            action.payload.initialMessage,
        );
        yield call(api.initApi, response.data.token, response.data.channel);
        yield put(botConvoStarted(response.data));
        yield call(saveToLocalStorage, response.data);
    } catch (e) {
        if (e.response && e.response.status === 409) {
            yield call(resetSenderId);

            // retry starting the convo now that we have a different senderId. Only do this once to
            // avoid an infinite loop.
            if (!isRetry) return yield call(startConvo, true, action);
        }

        if (e.response && e.response.status === 401) {
            // Stop further calls to bot start route
            yield put(visitorBlacklisted());
            yield put(removeConversation(true));  
        } else {
            yield put(convoStartError());
        }

    }
}

export function* startLockedPricingBot(
    action: StartLockedPricingBotAction,
    isRetry: boolean = false,
): any {
    try {
        const convo: ConversationState = yield select(getConvo);
        if (convo.active || (!convo.active && convo.ended)) return;

        const visitor: VisitorState = yield select(getVisitor);
        if (visitor.blacklisted) {
            return;
        }
        yield put(botConvoStarting());
        const { botId, customMetadata } = yield select(getSettings);
        const { id: locationId } = yield select(getLocation);
        const response: {
            data: BotConvoStartedPayload;
        } = yield call(
            api.startLockedPricingConvo,
            locationId,
            botId,
            visitor,
            action.payload,
            customMetadata,
        );
        yield call(api.initApi, response.data.token, response.data.channel);
        yield put(botConvoStarted(response.data));
        yield call(saveToLocalStorage, response.data);
    } catch (e) {
        if (e.response && e.response.status === 409) {
            yield call(resetSenderId);
            // retry starting the convo now that we have a different senderId. Only do this once to
            // avoid an infinite loop.
            if (!isRetry) return yield call(startLockedPricingBot, action, true);
        }
        if (e.response && e.response.status === 401) {
            // Stop further calls to bot start route
            yield put(visitorBlacklisted());
            yield put(removeConversation(true));  
        } else {
            yield put(convoStartError());
        }
    }
}

/**
 * Handles proactive actions.
 */
export function* startProactiveSaga(): any {
    try {
        const { proactiveTimer } = yield select(getSettings);
        const parsedTimer = parseFloat(proactiveTimer);
        if (!isFinite(parsedTimer) || parsedTimer <= 0) {
            return;
        }

        const { declinedProactive } = yield select(getVisitor);
        if (declinedProactive && declinedProactive + PROACTIVE_DECLINE_TIMEOUT > Date.now()) {
            return;
        }

        yield call(delay, parsedTimer * 1000);

        const convo: ConversationState = yield select(getConvo);
        if (convo.active || (!convo.active && convo.ended)) {
            return;
        }

        yield call(incentiveSaga);
    } catch (e) {
        return;
    }
}

/**
 * Handles a user affirmatively clicking on the current proactive offer.
 */
export function* handleProactiveClicked(): any {
    const { proactive } = yield select(getUi);
    if (proactive) {
        window.DIConv.startConversationWithVisitorMessage(proactive.invokeMessage);
        yield put(proactiveRemove());
    }
}

/**
 * Handles a user negatively closing on the current proactive offer.
 */
export function* handleProactiveClosed(): any {
    yield put(proactiveRemove());
    yield apply(
        LocalStorageManager,
        LocalStorageManager.addData,
        [{ declinedProactive: Date.now() }],
    );
}

function* saveToLocalStorage(data: BotConvoStartedPayload) {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.addData,
        [
            {
                hasConvo: true,
                visitorToken: data.token,
                channel: data.channel,
            },
        ],
    );
    const localData = yield apply(manager, manager.getData);

    yield put(setLocalSettings(localData));
}

/**
 * we already have an existing bot convo with this senderId but don't have the correct info due
 * to some unknown reason, so reset/change the senderId so they can actually continue
 */
export function* resetSenderId() {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.resetSenderId,
    );
    const localData = yield apply(manager, manager.getData);
    yield put(setLocalSettings(localData));
}

export default function* startSaga() {
    yield takeEvery(actionTypes.CTA_CLICKED, startConvo, false);
    yield takeEvery(actionTypes.PROACTIVE_CLICKED, handleProactiveClicked);
    yield takeEvery(actionTypes.PROACTIVE_CLOSED, handleProactiveClosed);
    yield takeEvery(actionTypes.CONVO_WITH_MESSAGE_START_REQUEST, startConvo, false);
    yield takeEvery(actionTypes.START_LOCKED_PRICING_BOT, startLockedPricingBot);
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/sagas/startSaga.ts